import { useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { hrApi } from '@/api';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { HrAttendanceSegmentBreakdown } from '@/modules/hr/components/HrAttendanceSegmentBreakdown';
import {
  hrFormatAttendanceClock,
  hrFormatMinutes,
  hrFormatRecordDaysLabel,
  hrIsApprovedOvertimeStatus,
} from '@/modules/hr/components/hrCalendarUtils';
import { HrWorkflowStatusIcon } from '@/modules/hr/components/HrWorkflowStatusIcon';
import { hrRecordStatusLabel } from '@/modules/hr/components/hrStatusUtils';
import type { HrOvertimeRequest } from '@/types/hr';
import { tierOptions } from '@/lib/query';
import { cn } from '@/utils/cn';

type Props = {
  overtimeId: number;
  /** When the parent already has the overtime row, skip the overtime fetch. */
  overtime?: HrOvertimeRequest | null;
  compact?: boolean;
  className?: string;
  /** When false, skip hours breakdown (e.g. parent already renders it). */
  showSegments?: boolean;
};

function attendanceModeBadgeVariant(mode?: string): 'default' | 'success' | 'warning' | 'info' {
  switch (mode?.trim().toUpperCase()) {
    case 'GEO':
      return 'success';
    case 'WEB':
    case 'MOBILE':
      return 'info';
    case 'BIOMETRIC':
    case 'RFID':
    case 'QR':
      return 'warning';
    default:
      return 'default';
  }
}

function attendanceModeLabel(mode: string | null | undefined, t: TFunction) {
  const normalized = mode?.trim().toUpperCase() || 'MANUAL';
  const fallback = normalized
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, (letter) => letter.toUpperCase());
  return t(`attendance.modes.${normalized}`, { defaultValue: fallback });
}

/**
 * Overtime summary with linked attendance check-in / check-out times and hours
 * breakdown — same layout as the calendar day popup and attendance timeline.
 */
export function OvertimeApprovalContextDetails({
  overtimeId,
  overtime: overtimeProp,
  compact = false,
  className,
  showSegments = true,
}: Props) {
  const { t } = useTranslation(['hr', 'workflow']);
  const { data: fetchedOvertime, isLoading: overtimeLoading } = useQuery({
    queryKey: ['hr', 'overtime', overtimeId, 'context-details'],
    queryFn: () => hrApi.getOvertime(overtimeId),
    enabled: overtimeId > 0 && !overtimeProp,
    ...tierOptions('operational'),
  });

  const overtime = overtimeProp ?? fetchedOvertime ?? null;
  const attendanceId = overtime?.attendanceId ?? null;

  const { data: attendance, isLoading: attendanceLoading } = useQuery({
    queryKey: ['hr', 'attendance', attendanceId, 'overtime-context'],
    queryFn: () => hrApi.getAttendance(attendanceId!),
    enabled: attendanceId != null && attendanceId > 0,
    ...tierOptions('operational'),
  });

  if (overtimeLoading || (attendanceId != null && attendanceLoading && !attendance)) {
    return <Skeleton className={cn('h-24 w-full rounded-lg', className)} />;
  }

  if (!overtime) return null;

  const approved = hrIsApprovedOvertimeStatus(overtime.statusCode);

  return (
    <div
      className={cn(
        compact
          ? 'space-y-2'
          : 'rounded-lg border border-slate-200 bg-slate-50/80 p-3 dark:border-slate-700 dark:bg-slate-900/40',
        className,
      )}
    >
      <dl className="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
        <dt className="text-slate-500">{t('attendance.detail.approval')}</dt>
        <dd className="font-medium text-slate-900 dark:text-slate-100">
          <span className="inline-flex items-center gap-1.5">
            <HrWorkflowStatusIcon
              statusCode={approved ? 'APPROVED' : overtime.statusCode ?? 'PENDING'}
            />
            {hrRecordStatusLabel(overtime)}
          </span>
        </dd>
        <dt className="text-slate-500">{t('attendance.kind.overtime')}</dt>
        <dd>
          {hrFormatMinutes(overtime.minutes ?? 0)}
          {overtime.overtimeType ? ` · ${overtime.overtimeType}` : ''}
        </dd>
        <dt className="text-slate-500">{t('attendance.detail.date')}</dt>
        <dd>
          {hrFormatRecordDaysLabel({
            startDate: overtime.overtimeDate,
            endDate: overtime.overtimeDate,
          })}
        </dd>
        {attendance ? (
          <>
            <dt className="text-slate-500">{t('attendance.detail.checkIn')}</dt>
            <dd>{hrFormatAttendanceClock(attendance.checkIn)}</dd>
            <dt className="text-slate-500">{t('attendance.detail.source')}</dt>
            <dd>
              <Badge variant={attendanceModeBadgeVariant(attendance.checkInMode)}>
                {attendanceModeLabel(attendance.checkInMode, t)}
              </Badge>
            </dd>
            <dt className="text-slate-500">{t('attendance.detail.checkOut')}</dt>
            <dd>{hrFormatAttendanceClock(attendance.checkOut)}</dd>
          </>
        ) : null}
      </dl>
      {showSegments && attendanceId != null ? (
        <HrAttendanceSegmentBreakdown
          attendanceId={attendanceId}
          enabled={Boolean(attendance?.checkOut ?? true)}
        />
      ) : null}
    </div>
  );
}
